import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { messagesService } from '../services/messagesService';
import { supabase } from '../lib/supabase';

export default function Conversations() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (!user) {
      navigate('/login');
      return;
    }
    loadConversations();
  }, [user, navigate]);

  const loadConversations = async () => {
    try {
      setLoading(true);
      setError('');
      const convData = await messagesService.getConversations(user.id);
      
      // Load additional data for each conversation
      const conversationsWithData = await Promise.all(
        convData.map(async (conv) => {
          try {
            // Load listing
            const { data: listing } = await supabase
              .from('listings')
              .select('*')
              .eq('id', conv.listing_id)
              .single();

            // Load other user profile
            const otherUserId = user.id === conv.buyer_id ? conv.seller_id : conv.buyer_id;
            const { data: otherUser, error: otherUserError } = await supabase
              .from('profiles')
              .select('*')
              .eq('id', otherUserId)
              .single();

            // Use fallback if profile doesn't exist or can't be accessed
            const finalOtherUser = otherUser || {
              id: otherUserId,
              name: `User ${otherUserId.slice(0, 8)}`,
              email: 'user@example.com'
            };
            
            console.log('Loaded user profile:', { otherUserId, otherUser, otherUserError, finalOtherUser });

            return {
              ...conv,
              listing,
              otherUser: finalOtherUser
            };
          } catch (err) {
            console.error('Error loading conversation data:', err);
            return conv;
          }
        })
      );
      
      setConversations(conversationsWithData);
    } catch (err) {
      console.error('Error loading conversations:', err);
      setError('Failed to load conversations. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffInHours = (now - date) / (1000 * 60 * 60);

    if (diffInHours < 1) {
      return 'Just now';
    } else if (diffInHours < 24) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffInHours < 48) {
      return 'Yesterday';
    } else {
      return date.toLocaleDateString();
    }
  };

  const getOtherUser = (conversation) => {
    return conversation.otherUser;
  };


  const getListingTitle = (listing) => {
    return listing?.title || 'Unknown Listing';
  };

  if (loading) {
    return (
      <div className="container-center mx-auto max-w-2xl">
        <div className="pt-8 pb-6 sm:pt-12 sm:pb-8">
          <div className="space-y-4">
            {[1, 2, 3].map((i) => (
              <div key={i} className="bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-white/40">
                <div className="flex items-center space-x-4">
                  <div className="w-12 h-12 bg-slate-200 rounded-full loading-skeleton"></div>
                  <div className="flex-1">
                    <div className="h-4 bg-slate-200 rounded loading-skeleton mb-2"></div>
                    <div className="h-3 bg-slate-200 rounded loading-skeleton w-3/4"></div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-center mx-auto max-w-2xl">
        <div className="pt-8 pb-6 sm:pt-12 sm:pb-8">
          <div className="bg-red-50 border border-red-200 rounded-xl p-4">
            <p className="text-red-700">{error}</p>
            <button
              onClick={loadConversations}
              className="mt-2 text-red-600 hover:text-red-800 font-medium"
            >
              Try Again
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="container-center mx-auto max-w-2xl">
      <div className="pt-8 pb-6 sm:pt-12 sm:pb-8">
        {conversations.length === 0 ? (
          <div className="text-center py-12">
            <div className="text-6xl mb-4">💬</div>
            <h2 className="text-xl font-semibold text-slate-700 mb-2">No messages yet</h2>
            <p className="text-slate-500 mb-6">
              Start a conversation by messaging about a listing you're interested in.
            </p>
            <Link
              to="/browse"
              className="btn-primary inline-flex items-center"
            >
              Browse Listings
            </Link>
          </div>
        ) : (
          <div className="space-y-3">
            {conversations.map((conversation) => {
              const otherUser = getOtherUser(conversation);
              const listing = conversation.listing;
              
              return (
                <Link
                  key={conversation.id}
                  to={`/messages/${conversation.id}`}
                  className="block bg-white/80 backdrop-blur-sm rounded-2xl p-4 border border-white/40 hover:shadow-lg transition-all duration-200 hover:-translate-y-0.5"
                >
                  <div className="flex items-center space-x-4">
                    {/* Avatar */}
                    {otherUser?.avatar_url ? (
                      <img src={otherUser.avatar_url} alt="Avatar" className="w-12 h-12 rounded-full object-cover" />
                    ) : (
                      <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold text-lg">
                        {otherUser?.name ? otherUser.name.charAt(0).toUpperCase() : '?'}
                      </div>
                    )}

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <h3 className="font-semibold text-slate-900 truncate">
                          {otherUser?.name || 'Unknown User'}
                        </h3>
                        <span className="text-xs text-slate-500">
                          {formatTime(conversation.last_message_at)}
                        </span>
                      </div>
                      
                      <p className="text-sm text-slate-600 truncate mb-1">
                        About: {getListingTitle(listing)}
                      </p>
                      
                      {listing && (
                        <div className="flex items-center space-x-2">
                          {listing.photos?.[0] && (
                            <img
                              src={listing.photos[0]}
                              alt={listing.title}
                              className="w-8 h-8 rounded object-cover"
                            />
                          )}
                          <span className="text-sm font-medium text-green-600">
                            ${(listing.price_cents / 100).toFixed(2)}
                          </span>
                        </div>
                      )}
                    </div>

                    {/* Status indicator */}
                    <div className="w-3 h-3 bg-blue-500 rounded-full"></div>
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
